import { memo, useMemo } from 'react'
import { Box, Typography, Tooltip } from '@mui/material'
import { Security } from '../../proto/generated/security'
import { surveillanceMap } from '../quickTradeWindow/surveillanceMap'
import AlertIcon from '../../assets/icons/AlertIcon'
import InfoIcon from '../../assets/icons/InfoIcon'

interface SurveillanceIndicatorCardProps {
    security: Security
}

const SurveillanceIndicatorCard = ({ security }: SurveillanceIndicatorCardProps) => {
    const surveillanceInfo = useMemo(() => {
        const surveillanceCode = security?.surveillanceIndicator
        if (!surveillanceCode || surveillanceCode === 0) return null

        const surveillanceData = surveillanceMap[surveillanceCode]
        if (!surveillanceData) return null

        const [label, message] = surveillanceData

        return {
            code: surveillanceCode,
            label,
            fullMessage: message || label
        }
    }, [security?.surveillanceIndicator])

    if (!surveillanceInfo) return null

    return (
        <Box
            sx={(theme) => ({
                backgroundColor: theme.palette.background.paper,
                borderRadius: 1,
                p: theme.spacing(0.75, 1),
                mb: theme.spacing(1.5),
                border: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                flexDirection: 'row',
                gap: theme.spacing(1),
                '&:hover': {
                    backgroundColor: theme.palette.background.default
                }
            })}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 32
                }}
            >
                <Box
                    sx={(theme) => ({
                        fontSize: 20,
                        color: theme.palette.warning.main,
                        display: 'flex',
                        alignItems: 'center'
                    })}
                >
                    <AlertIcon />
                </Box>
            </Box>

            <Box
                sx={(theme) => ({
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.spacing(0.25)
                })}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <Typography variant="subtitle2" component="h3">
                        Regulatory Caution
                    </Typography>
                    <Tooltip
                        title={surveillanceInfo.fullMessage}
                        arrow
                        placement="top"
                    >
                        <Box
                            sx={(theme) => ({
                                fontSize: 14,
                                color: theme.palette.text.secondary,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                '&:hover': {
                                    color: theme.palette.text.primary
                                }
                            })}
                        >
                            <InfoIcon />
                        </Box>
                    </Tooltip>
                </Box>

                <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ textOverflow: 'ellipsis', lineHeight: 1.4 }}
                >
                    {surveillanceInfo.label}
                </Typography>
            </Box>
        </Box>
    )
}

export default memo(SurveillanceIndicatorCard)
